import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ActiveCompanyProvider } from "@/context/ActiveCompanyContext";
import { ScenarioModeProvider } from "@/context/ScenarioModeContext";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import GroupOverviewPage from "@/pages/group/GroupOverviewPage";
import GroupOverheadPage from "@/pages/group/GroupOverheadPage";
import IntercompanyPage from "@/pages/group/IntercompanyPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import ResourcePlanningPage from "@/pages/planning/ResourcePlanningPage";
import RevenuePage from "@/pages/planning/RevenuePage";
import PersonnelCostsPage from "@/pages/planning/PersonnelCostsPage";
import IndirectCostsPage from "@/pages/planning/IndirectCostsPage";
import PnlPage from "@/pages/planning/PnlPage";
import CashFlowPage from "@/pages/planning/CashFlowPage";
import ScenariosPage from "@/pages/planning/ScenariosPage";
import ActualImportPage from "@/pages/actuals/ActualImportPage";
import ActualsHistoryPage from "@/pages/actuals/ActualsHistoryPage";
import PeriodClosePage from "@/pages/actuals/PeriodClosePage";
import CompaniesPage from "@/pages/admin/CompaniesPage";
import PeoplePage from "@/pages/admin/PeoplePage";
import CustomersPage from "@/pages/admin/CustomersPage";
import CostAccountsPage from "@/pages/admin/CostAccountsPage";
import WorkingHoursPage from "@/pages/admin/WorkingHoursPage";
import FinancialParametersPage from "@/pages/admin/FinancialParametersPage";
import PaymentTermsPage from "@/pages/admin/PaymentTermsPage";
import VatPage from "@/pages/admin/VatPage";
import ConsolidatedPnlPage from "@/pages/group/ConsolidatedPnlPage";
import DividendsPage from "@/pages/planning/DividendsPage";
import OwnershipPage from "@/pages/admin/OwnershipPage";
import AccountsPage from "@/pages/admin/AccountsPage";

function Protected() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]" data-testid="loading-screen">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell />;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ActiveCompanyProvider>
        <ScenarioModeProvider>
        <TooltipProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route element={<Protected />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/planning/resource-planning" element={<ResourcePlanningPage />} />
              <Route path="/planning/revenue" element={<RevenuePage />} />
              <Route path="/planning/personnel-costs" element={<PersonnelCostsPage />} />
              <Route path="/planning/indirect-costs" element={<IndirectCostsPage />} />
              <Route path="/planning/scenarios" element={<ScenariosPage />} />
              <Route path="/planning/dividends" element={<DividendsPage />} />
              <Route path="/financials/pnl" element={<PnlPage />} />
              <Route path="/financials/cash-flow" element={<CashFlowPage />} />
              <Route path="/actuals/import" element={<ActualImportPage />} />
              <Route path="/actuals/history" element={<ActualsHistoryPage />} />
              <Route path="/actuals/period-close" element={<PeriodClosePage />} />
              <Route path="/group/overview" element={<GroupOverviewPage />} />
              <Route path="/group/overhead" element={<GroupOverheadPage />} />
              <Route path="/group/intercompany" element={<IntercompanyPage />} />
              <Route path="/group/consolidation" element={<ConsolidatedPnlPage />} />
              <Route path="/admin/companies" element={<CompaniesPage />} />
              <Route path="/admin/people" element={<PeoplePage />} />
              <Route path="/admin/customers" element={<CustomersPage />} />
              <Route path="/admin/cost-accounts" element={<CostAccountsPage />} />
              <Route path="/admin/working-hours" element={<WorkingHoursPage />} />
              <Route path="/admin/financial-parameters" element={<FinancialParametersPage />} />
              <Route path="/admin/payment-terms" element={<PaymentTermsPage />} />
              <Route path="/admin/vat" element={<VatPage />} />
              <Route path="/admin/ownership" element={<OwnershipPage />} />
              <Route path="/admin/accounts" element={<AccountsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        </TooltipProvider>
        </ScenarioModeProvider>
        </ActiveCompanyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
